import { RiArrowLeftLine, RiLoader3Fill } from '@remixicon/react';
import {
  FormEventHandler,
  HTMLInputAutoCompleteAttribute,
  ReactNode,
} from 'react';
import { Link } from 'react-router';
import SideActionsList, { SideActionItem } from './SideActionsList';
import { useLanguage } from '../contexts/language';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
} from '@mui/material';
import SectionHeader from './SectionHeader';

interface MainSectionTemplateProps {
  children?: ReactNode;
  section?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  afterTitleContent?: ReactNode;
  isLoading?: boolean;
}

export const MainSectionTemplate = ({
  section,
  title,
  subtitle,
  afterTitleContent,
  isLoading,
  actions,
  children,
}: MainSectionTemplateProps) => (
  <div className="grid grid-rows-[auto_1fr] h-full">
    <SectionHeader
      className="mb-4"
      sideContent={actions}
      belowContent={afterTitleContent}
    >
      <div className="flex gap-4 items-end">
        <div>
          {section && <h2 className="h-[24px] font-medium">{section}</h2>}
          <h1 className="text-4xl">{title}</h1>
          {subtitle && <h3 className="text-base mt-2">{subtitle}</h3>}
        </div>
        {isLoading && <RiLoader3Fill className="animate-spin size-8" />}
      </div>
    </SectionHeader>
    <div className="min-h-0 overflow-auto pb-4">
      <div className="main-content">{children}</div>
    </div>
  </div>
);

interface FormSectionTemplate {
  children?: ReactNode;
  title: string;
  titleActions?: ReactNode;
  isLoading?: boolean;
  goBackLinkText?: string;
  goBackLinkUrl?: string;
  onSubmit: FormEventHandler;
  autoComplete?: HTMLInputAutoCompleteAttribute;
  actions?: ReactNode;
}

export const FormSectionTemplate = ({
  goBackLinkText,
  goBackLinkUrl,
  onSubmit,
  title,
  titleActions,
  actions,
  children,
  isLoading = false,
  autoComplete = 'off',
}: FormSectionTemplate) => {
  return (
    <form
      onSubmit={onSubmit}
      className="grid grid-rows-[auto_1fr] grid-cols-[1fr] h-full"
      autoComplete={autoComplete}
    >
      <div>
        <div className="main-content min-h-[56px] flex items-center justify-start">
          {goBackLinkText && goBackLinkUrl && (
            <nav className="py-2">
              <Link
                to={goBackLinkUrl}
                className="inline-flex gap-1 items-center h-[24px]"
              >
                <RiArrowLeftLine className="h-[18px] w-[18px]" />
                <span className="underline underline-offset-2">
                  {goBackLinkText}
                </span>
              </Link>
            </nav>
          )}
        </div>
      </div>
      <div>
        <div className="main-content max-w-[600px]">
          <Card variant="outlined">
            <CardHeader
              title={
                <div className="flex items-center gap-4">
                  <h1 className="text-4xl">{title}</h1>
                  {isLoading && (
                    <RiLoader3Fill className="animate-spin size-8" />
                  )}
                  {titleActions && (
                    <div className="ml-auto flex items-center gap-4">
                      {titleActions}
                    </div>
                  )}
                </div>
              }
            />
            <CardContent>{children}</CardContent>
            {actions && <CardActionArea>{actions}</CardActionArea>}
          </Card>
        </div>
      </div>
    </form>
  );
};

interface LeftPanelSectionTemplateProps {
  title: ReactNode;
  isLoading?: boolean;
  leftPanelActions?: SideActionItem[];
  children?: ReactNode;
  actions?: ReactNode;
  goBackLinkText?: string;
  goBackLinkUrl?: string;
}

export const PANEL_CARD_INNER_SPACING_CLASSNAME = 'p-2 md:p-4';

export const LeftPanelSectionTemplate = ({
  title,
  children,
  actions,
  isLoading,
  leftPanelActions = [],
  goBackLinkText,
  goBackLinkUrl,
}: LeftPanelSectionTemplateProps) => {
  return (
    <div className="grid grid-rows-[auto_1fr] h-full pb-4">
      <SectionHeader className="mb-8" sideContent={actions}>
        <div className="flex gap-3 md:gap-4 items-center">
          <div>
            {goBackLinkText && goBackLinkUrl && (
              <nav className="lg:translate-x-[-22px]">
                <Link
                  to={goBackLinkUrl}
                  className="inline-flex gap-[4px] items-center h-[24px] font-medium underline underline-offset-2"
                >
                  <RiArrowLeftLine className="h-[18px] w-[18px]" />
                  <span className="underline underline-offset-2">
                    {goBackLinkText}
                  </span>
                </Link>
              </nav>
            )}
            <h4 className="text-2xl md:text-4xl">{title}</h4>
          </div>
          {isLoading && (
            <RiLoader3Fill className="animate-spin size-6 md:size-8" />
          )}
        </div>
      </SectionHeader>

      <div>
        <div className="main-content grid grid-rows-[min-content_1f] md:grid-rows-[auto] md:grid-cols-[20%_1fr] gap-2 md:gap-8 md:h-full ">
          <Card className={PANEL_CARD_INNER_SPACING_CLASSNAME}>
            <SideActionsList className='h-full' actions={leftPanelActions} />
          </Card>
          <section>{children}</section>
        </div>
      </div>
    </div>
  );
};

export const MainPanelForm = ({
  actions,
  children,
  onSubmit,
  className,
}: {
  children?: ReactNode;
  actions?: ReactNode;
  onSubmit?: FormEventHandler;
  className?: string;
}) => (
  <Card className="grid grid-rows-[1fr] grid-cols-[1fr] h-full overflow-visible">
    <form
      onSubmit={onSubmit}
      className={['h-full grid grid-rows-[1fr_min-content]', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[PANEL_CARD_INNER_SPACING_CLASSNAME, actions && 'pb-0']
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
      {actions && (
        <div className="sticky md:static bottom-0 overflow-hidden rounded-s-[8px]">
          <div
            className={`${PANEL_CARD_INNER_SPACING_CLASSNAME} md:pt-0 flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-end md:gap-4 bg-white md:bg-transparent`}
          >
            {actions}
          </div>
        </div>
      )}
    </form>
  </Card>
);

export const CommonMainPanelForm = ({
  onCancelEdition,
  onEditionRequest,
  cancelButtonLabel,
  children,
  className,
  editButtonLabel,
  isEditing = false,
  isEditDisabled = false,
  isSubmitDisabled = false,
  isSubmitting = false,
  onSubmit,
  submitButtonLabel,
}: {
  children?: ReactNode;
  isEditing?: boolean;
  onSubmit: FormEventHandler;
  onCancelEdition: () => void;
  onEditionRequest: () => void;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  editButtonLabel?: string;
  className?: string;
  isSubmitDisabled?: boolean;
  isSubmitting?: boolean;
  isEditDisabled?: boolean;
}) => {
  const { t } = useLanguage();
  return (
    <MainPanelForm
      className={className}
      onSubmit={onSubmit}
      actions={
        isEditing ? (
          <>
            <Button type="button" onClick={onCancelEdition}>
              {cancelButtonLabel || t('content.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={isSubmitDisabled}
              loading={isSubmitting}
            >
              {submitButtonLabel || t('content.save')}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            disabled={isEditDisabled}
            onClick={onEditionRequest}
          >
            {editButtonLabel || t('content.edit')}
          </Button>
        )
      }
    >
      {children}
    </MainPanelForm>
  );
};
